import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  CheckCircle as VerifyIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import platformService from '../../services/platformService';

export default function PendingVerification() {
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');

  // Reject Dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTenantId, setRejectTenantId] = useState(null);
  const [rejectTenantName, setRejectTenantName] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  // Verify loading state
  const [verifyingId, setVerifyingId] = useState(null);

  // Fetch pending tenants
  const fetchTenants = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await platformService.getPendingTenants({
        page: page + 1,
        limit: rowsPerPage,
        search
      });

      if (response.success) {
        setTenants(response.data.tenants);
        setTotal(response.data.pagination.total);
      }
    } catch (err) {
      setError('Failed to load pending verifications');
      console.error('Fetch pending tenants error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [page, rowsPerPage, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleVerify = async (tenantId, tenantName) => {
    if (!window.confirm(`Verify "${tenantName}"? They will be able to access the system.`)) {
      return;
    }

    setVerifyingId(tenantId);
    try {
      const response = await platformService.verifyTenant(tenantId);
      if (response.success) {
        toast.success(`"${tenantName}" verified successfully`);
        fetchTenants();
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to verify tenant';
      toast.error(message);
    } finally {
      setVerifyingId(null);
    }
  };

  const handleRejectClick = (tenantId, tenantName) => {
    setRejectTenantId(tenantId);
    setRejectTenantName(tenantName);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectDialogClose = () => {
    setRejectDialogOpen(false);
    setRejectTenantId(null);
    setRejectTenantName('');
    setRejectReason('');
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setRejectLoading(true);
    try {
      const response = await platformService.rejectTenant(rejectTenantId, rejectReason);
      if (response.success) {
        toast.success(`"${rejectTenantName}" rejected`);
        handleRejectDialogClose();
        fetchTenants();
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to reject tenant';
      toast.error(message);
    } finally {
      setRejectLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
            Pending Verification
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve new company registrations
          </Typography>
        </Box>
        <IconButton onClick={fetchTenants} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by company name or email..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Card>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 400, sm: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Company Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Users</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Registered</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No pending verifications</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tenants.map((tenant) => (
                  <TableRow key={tenant.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {tenant.company_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {tenant.billing_email || tenant.details?.email || '-'}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {tenant.userCount}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/tenants/${tenant.id}`)}
                          title="View Details"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleVerify(tenant.id, tenant.company_name)}
                          disabled={verifyingId === tenant.id}
                          title="Verify"
                        >
                          {verifyingId === tenant.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <VerifyIcon />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRejectClick(tenant.id, tenant.company_name)}
                          title="Reject"
                        >
                          <RejectIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleRejectDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Company</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to reject <strong>{rejectTenantName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejection. This may be visible to the company.
          </Typography>
          <TextField
            fullWidth
            label="Rejection Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            rows={3}
            required
            placeholder="e.g., Incomplete information, Invalid business details..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejectLoading || !rejectReason.trim()}
          >
            {rejectLoading ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
